import { Component, OnInit, OnDestroy, inject, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TimeconversionService, ToastMessageService ,ClassTypeService, AppointmentTypesService} from '@fitness-force/api';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { TENANTS_INFO } from '@fitness-force/state';
import { MilestoneConfigService } from '../../services/milestone-config.service';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { AuditTrailMiniComponent } from '@fitness-force/audit-trail';
import {
  CreateMilestoneConfigRequest,
  UpdateMilestoneConfigRequest,
  MilestoneType,
  MilestoneComparisonType,
  MilestoneUnit,
  MilestonePeriodType,
  MilestoneRewardType
} from '../../models/milestone.models';

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'lib-create-edit-milestone-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    InputNumberModule,
    DatePickerModule,
    MultiSelectModule,
    TranslateModule,
    BreadcrumbComponent,
    AuditTrailMiniComponent,
  ],
  templateUrl: './create-edit-milestone-config.component.html',
  styleUrls: ['./create-edit-milestone-config.component.scss']
})
export class CreateEditMilestoneConfigComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly milestoneService = inject(MilestoneConfigService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastMessageService);
  private readonly classTypeService = inject(ClassTypeService);
  private readonly appointmentTypeService = inject(AppointmentTypesService);
  private readonly store = inject(Store);
  private readonly timeConversionService = inject(TimeconversionService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly cd = inject(ChangeDetectorRef);
  
  private readonly destroy$ = new Subject<void>();
  private readonly tenantId = signal<number>(0);
  showAuditLogsFeature = false;

  milestoneForm!: FormGroup;
  loading = signal(false);
  editMode = signal(false);
  configId = signal<string | null>(null);

  milestoneTypeOptions: DropdownOption[] = [
    { label: 'Class Count', value: MilestoneType.ClassCount },
    { label: 'Distance', value: MilestoneType.Distance },
    { label: 'Appointment Count', value: MilestoneType.AppointmentCount },
    { label: 'Total Session Count', value: MilestoneType.TotalSessionCount }
  ];

  comparisonTypeOptions: DropdownOption[] = [
    { label: 'Greater Than or Equal', value: MilestoneComparisonType.GreaterThanOrEqual },
    { label: 'Equal', value: MilestoneComparisonType.Equal }
  ];

  unitOptions = signal<DropdownOption[]>([]);

  periodTypeOptions: DropdownOption[] = [
    { label: 'Lifetime', value: MilestonePeriodType.Lifetime },
    { label: 'Weekly', value: MilestonePeriodType.Weekly },
    { label: 'Monthly', value: MilestonePeriodType.Monthly },
    { label: 'Custom', value: MilestonePeriodType.Custom }
  ];

  rewardTypeOptions: DropdownOption[] = [
    { label: 'Badge', value: MilestoneRewardType.Badge },
    { label: 'Points', value: MilestoneRewardType.Points },
    { label: 'Discount', value: MilestoneRewardType.Discount }
  ];

  weekStartDayOptions: DropdownOption[] = [
    { label: 'Sunday', value: '0' },
    { label: 'Monday', value: '1' },
    { label: 'Tuesday', value: '2' },
    { label: 'Wednesday', value: '3' },
    { label: 'Thursday', value: '4' },
    { label: 'Friday', value: '5' },
    { label: 'Saturday', value: '6' }
  ];

  
  monthStartDayOptions: DropdownOption[] = Array.from({ length: 31 }, (_, i) => ({
    label: `${i + 1}`,
    value: `${i + 1}`
  }));

  // Dynamic options loaded from API
  classTypeOptions = signal<DropdownOption[]>([]);
  appointmentTypeOptions = signal<DropdownOption[]>([]);
  loadingClassTypes = signal(false);
  loadingAppointmentTypes = signal(false);

  // Expose enum for template
  MilestoneType = MilestoneType;
  MilestonePeriodType = MilestonePeriodType;

  ngOnInit(): void {
    this.featureFlagService.getFlag$('show_audit_trial').pipe(takeUntil(this.destroy$)).subscribe((showAudit) => {
      this.showAuditLogsFeature = showAudit;
      this.cd.detectChanges();
    });
    this.buildForm();
    this.setupMilestoneTypeListener();
    this.setupPeriodTypeListener();
    this.setupTenantSubscription();
    this.loadCompanyClassTypes();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.editMode.set(true);
      this.configId.set(id);
      this.loadMilestoneConfig(id);
    } else {
      this.updateUnitOptions(MilestoneType.ClassCount);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupTenantSubscription(): void {
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant: any) => {
        if (tenant?.selected_tenant?.id) {
          this.tenantId.set(tenant.selected_tenant.id);
          this.loadTenantAppointmentTypes();
        }
      });
  }

  private loadCompanyClassTypes(): void {
    this.loadingClassTypes.set(true);
    this.classTypeService.getCompanyLevelClassTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (classTypes: any[]) => {
          const options = classTypes
            .map((ct: any) => ({
              label: ct.name,
              value: ct.name
            }));
          this.classTypeOptions.set(options);
          this.loadingClassTypes.set(false);
        },
        error: (error:any) => {
          this.toastService.showError(error);
          this.loadingClassTypes.set(false);
        }
      });
  }

  private loadTenantAppointmentTypes(): void {
    const tenantId = this.tenantId();
    if (!tenantId) return;

    this.loadingAppointmentTypes.set(true);
    this.appointmentTypeService.getAppointmentTypes(tenantId, [])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const options = (response.items || [])
            
            .map((at: any) => ({
              label: at.name_primary_language,
              value: at.name_primary_language
            }));
          this.appointmentTypeOptions.set(options);
          this.loadingAppointmentTypes.set(false);
        },
        error: (error:any) => {
          this.toastService.showError(error);
          this.loadingAppointmentTypes.set(false);
        }
      });
  }

  buildForm(): void {
    this.milestoneForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      tierName: ['', Validators.maxLength(100)],
      milestoneType: [MilestoneType.ClassCount, Validators.required],
      comparisonType: [MilestoneComparisonType.GreaterThanOrEqual, Validators.required],
      targetValue: [1, [Validators.required, Validators.min(1)]],
      unit: [MilestoneUnit.Classes, Validators.required],
      periodType: [MilestonePeriodType.Lifetime, Validators.required],
      rewardType: [MilestoneRewardType.Badge, Validators.required],
      classTypeNames: [[]],
      appointmentTypeNames: [[]],
      startDate: [new Date(), Validators.required],
      weekStartDay: [null],
      monthStartDay: [null],
      customPeriodStart: [null],
      customPeriodEnd: [null]
    });
  }

  setupMilestoneTypeListener(): void {
    this.milestoneForm.get('milestoneType')?.valueChanges.subscribe((type: MilestoneType) => {
      this.updateUnitOptions(type);
      
      // Clear type-specific arrays when switching milestone types
      if (type !== MilestoneType.ClassCount) {
        this.milestoneForm.patchValue({ classTypeNames: [] });
      }
      if (type !== MilestoneType.AppointmentCount) {
        this.milestoneForm.patchValue({ appointmentTypeNames: [] });
      }
    });
  }

  setupPeriodTypeListener(): void {
    this.milestoneForm.get('periodType')?.valueChanges.subscribe((type: MilestonePeriodType) => {
      // Clear period-specific fields when switching period types
      if (type !== MilestonePeriodType.Weekly) {
        this.milestoneForm.patchValue({ weekStartDay: null });
        
      }
      if (type !== MilestonePeriodType.Monthly) {
        this.milestoneForm.patchValue({ monthStartDay: null });
      }
      if (type !== MilestonePeriodType.Custom) {
        this.milestoneForm.patchValue({ customPeriodStart: null, customPeriodEnd: null });
      }
    });
  }

  setupRewardTypeListener(): void {
    // Reward type listener is no longer needed as rewardValue doesn't exist in API
  }

  updateUnitOptions(type: MilestoneType): void {
    let units: DropdownOption[] = [];

    switch (type) {
      case MilestoneType.ClassCount:
        units = [{ label: 'Classes', value: MilestoneUnit.Classes }];
        this.milestoneForm.patchValue({ unit: MilestoneUnit.Classes });
        break;
      case MilestoneType.Distance:
        units = [
          { label: 'Kilometers', value: MilestoneUnit.Kilometers },
          { label: 'Miles', value: MilestoneUnit.Miles }
        ];
        this.milestoneForm.patchValue({ unit: MilestoneUnit.Kilometers });
        break;
      case MilestoneType.AppointmentCount:
        units = [{ label: 'Appointments', value: MilestoneUnit.Appointments }];
        this.milestoneForm.patchValue({ unit: MilestoneUnit.Appointments });
        break;
      case MilestoneType.TotalSessionCount:
        units = [{ label: 'Sessions', value: MilestoneUnit.Sessions }];
        this.milestoneForm.patchValue({ unit: MilestoneUnit.Sessions });
        break;
    }

    this.unitOptions.set(units);
  }

  loadMilestoneConfig(id: string): void {
    this.loading.set(true);

    this.milestoneService.getMilestoneConfiguration(id).subscribe({
      next: (config) => {
        // Update unit options based on milestone type
        if (config.milestoneType) {
          this.updateUnitOptions(config.milestoneType as MilestoneType);
        }
        
        this.milestoneForm.patchValue({
          name: config.name || '',
          tierName: config.tierName || '',
          milestoneType: config.milestoneType || MilestoneType.ClassCount,
          comparisonType: config.comparisonType || MilestoneComparisonType.GreaterThanOrEqual,
          targetValue: config.targetValue,
          unit: config.unit || MilestoneUnit.Classes,
          periodType: config.periodType || MilestonePeriodType.Lifetime,
          rewardType: config.rewardType || MilestoneRewardType.Badge,
          classTypeNames: config.classTypeNames || [],
          appointmentTypeNames: config.appointmentTypeNames || [],
          startDate: config.startDate ? this.timeConversionService.ConvertUTCToLocalDateTime(config.startDate) : null,
          weekStartDay: config.weekStartDay || null,
          monthStartDay: config.monthStartDay || null,
          customPeriodStart: config.customPeriodStart || null,
          customPeriodEnd: config.customPeriodEnd || null
        });

        // Mark form as pristine after loading data
        this.milestoneForm.markAsPristine();
        this.loading.set(false);
      },
      error: (error:any) => {
        this.toastService.showError(error);
        this.loading.set(false);
        this.router.navigate(['/admin/milestone-configurations']);
      }
    });
  }

  onSave(): void {
    if (this.milestoneForm.invalid) {
      this.markFormGroupTouched(this.milestoneForm);
      return;
    }

    this.loading.set(true);
    const formValue = this.milestoneForm.getRawValue();
    formValue.startDate = this.timeConversionService.ConvertLocalDateTimeToUTC(formValue.startDate);

    if (this.editMode()) {
      this.updateMilestone(formValue);
    } else {
      this.createMilestone(formValue);
    }
  }

  createMilestone(data: CreateMilestoneConfigRequest): void {
    this.milestoneService.createMilestoneConfiguration(data).subscribe({
      next: () => {
        this.milestoneForm.markAsPristine(); // Mark form as pristine after successful save
        this.toastService.showSuccess('Milestone configuration created successfully');
        this.router.navigate(['/admin/milestone-configurations']);
      },
      error: (error:any) => {
        this.toastService.showError(error);
        this.loading.set(false);
      }
    });
  }

  updateMilestone(data: UpdateMilestoneConfigRequest): void {
    const id = this.configId();
    if (!id) return;

    this.milestoneService.updateMilestoneConfiguration(id, data).subscribe({
      next: () => {
        this.milestoneForm.markAsPristine(); // Mark form as pristine after successful save
        this.toastService.showSuccess('Milestone configuration updated successfully');
        this.router.navigate(['/admin/milestone-configurations']);
      },
      error: (error:any) => {
        this.toastService.showError(error);
        this.loading.set(false);
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/milestone-configurations']);
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  canDeactivate(): boolean {
    return !this.milestoneForm.dirty || confirm('You have unsaved changes. Do you want to leave?');
  }
}
